import { Icon } from '#/components/Icon/Icon';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { BarChart } from '#/modules/salary/types/BarChart';
import type { LineChart } from '#/modules/salary/types/LineChart';
import type { MainChartData } from '#/modules/salary/types/MainChartData';
import { isDefined } from '#/utils/functional/type';

import { EstimatedRangeButton } from './EstimatedRangeButton';
import {
  MainChartLegendButtonGroupHolder,
  MainChartLegendContainer,
} from './MainChartLegend.styled';
import { SubmissionsButton } from './SubmissionsButton';

type MainChartLegendProps = {
  data: MainChartData;
  setData: (arg0: MainChartData) => void;
};

export function MainChartLegend({ data, setData }: MainChartLegendProps) {
  const { i18n } = useI18n();

  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const activeChart = [data.lineChart, data.barChart].find(
    (c) => c?.active,
  ).name;

  if (!isDefined(data)) return null;

  const updateData = (activeId: string) => {
    setData({
      ...data,
      lineChart: {
        ...(data.lineChart as LineChart),
        active: activeId === 'estimatedRange',
      },
      barChart: {
        ...(data.barChart as BarChart),
        active: activeId === 'submissions',
      },
    });
  };

  return (
    <MainChartLegendContainer>
      <MainChartLegendButtonGroupHolder>
        <EstimatedRangeButton
          type="button"
          disabled={!data.lineChart}
          qaId="toggle-estimated-range-layer"
          onClick={() => updateData('estimatedRange')}
          data-active={activeChart === 'estimatedRange' ? true : undefined}
        >
          {isDefined(data.lineChart)
            ? 'Estimated Range'
            : 'Estimated Range Not Available'}
        </EstimatedRangeButton>

        <SubmissionsButton
          type="button"
          disabled={!data.barChart}
          qaId="toggle-submissions-layer"
          onClick={() => updateData('submissions')}
          data-active={activeChart === 'submissions' ? true : undefined}
        >
          <span>
            {isDefined(data.barChart)
              ? 'Submissions'
              : 'Submissions Not Available'}
          </span>

          {!isDefined(data.barChart) ? (
            <Tooltip
              size="large"
              placement="top-left"
              content={
                <BodyMicro
                  style={{
                    letterSpacing: 0,
                    fontWeight: 'normal',
                    lineHeight: 'normal',
                  }}
                >
                  {i18n(
                    "We've collected submissions related to this title and similar titles, but there are fewer than 20 submissions matching your search criteria. Our machine learning system can estimate an expected salary range for this title, but there are not enough submissions to show meaningful results.",
                  )}
                </BodyMicro>
              }
            >
              <Icon
                name="question-circle"
                colorName="mediumContentGrey"
                size={19}
              />
            </Tooltip>
          ) : null}
        </SubmissionsButton>
      </MainChartLegendButtonGroupHolder>
    </MainChartLegendContainer>
  );
}
